import { useEffect, useRef, useState } from "react";

import { api } from "../api";
import { Badge, Button, Card, Heading, Input, Text, useToast } from "../ds";
import { fecha, hace } from "../formato";
import type { DemoLead, DetalleLead } from "../tipos";
import { useAccion } from "./acciones";
import s from "./Demo.module.css";

/* La demo privada de una inmobiliaria: el link para mandarle por Chatwoot y si la abrió.
 * "Ver" abre la demo con `?equipo=1`, que no cuenta como visita: si no, mirarla desde acá
 * se leería como que la inmobiliaria se interesó. */

const MOTIVOS: Record<string, string> = {
  link_invalido: "El link de portal que tenía no es el perfil de una inmobiliaria.",
  perfil_inexistente: "Su perfil en el portal ya no existe.",
  sin_aviso_apto: "No tiene en los portales un aviso que sirva: casa, departamento, PH, lote, local u oficina, con 4 fotos o más.",
  fotos_no_disponibles: "Las fotos del aviso no se pudieron bajar.",
  sin_otro_aviso: "No tiene otro aviso que sirva: ya se usaron todos los que tiene.",
  sin_perfil_zonaprop: "No la encontramos en ningún portal: no tiene perfil vinculado ni aparece en Zonaprop, Argenprop o BuscadorProp en su zona.",
  sin_perfil: "No tiene perfil en ningún portal: pegá el link de su perfil.",
  bloqueo: "El portal frenó la lectura.",
  no_armada_a_tiempo: "La PC no armó la demo a tiempo.",
  sin_conversacion: "No se encontró su conversación en Chatwoot.",
};

/* Los portales de `src/demos/portales.ts`, por su `fuente`. */
const PORTALES: Record<string, string> = { zonaprop: "Zonaprop", mercadolibre: "Mercado Libre", argenprop: "Argenprop", buscadorprop: "BuscadorProp" };
export const nombrePortal = (fuente: string) => PORTALES[fuente] ?? fuente;

export const motivoDemo = (motivo: unknown) => MOTIVOS[String(motivo)] ?? "No se pudo armar.";

/* Por qué la demo pedida con el botón no salió sola: un motivo conocido, o el error tal cual. */
export const motivoEnvioDemo = (motivo: unknown) => MOTIVOS[String(motivo)] ?? (typeof motivo === "string" && motivo ? `${motivo}.` : "No se pudo armar.");

/* Por qué no salió un pedido desde el panel. */
export function motivoPedido(resultado: string | null, motivo: string | null): string {
  if (resultado === "sin_demo") return motivoDemo(motivo);
  if (motivo === "bloqueo") return "El portal frenó la lectura. Probá de nuevo en unos minutos.";
  return motivo ? `Falló: ${motivo}. Probá de nuevo.` : "Falló. Probá de nuevo.";
}

/* Cada cuánto se mira si la PC ya armó la demo pedida. */
const REVISION_MS = 4000;

/* Pedir la demo desde el panel: la arma la PC (`demos:atender`), porque desde el servidor los
 * portales bloquean. Sin demo, "Generar demo"; con demo, "Otra propiedad" la rearma con otro
 * aviso y el mismo link. Si no se le conoce un perfil, o es otro, se pega el link. */
export function PedirDemo({ d, onCambio }: { d: DetalleLead; onCambio: () => void }) {
  const { pendiente: enCurso, ejecutar } = useAccion();
  const [conLink, setConLink] = useState(false);
  const [link, setLink] = useState("");
  const pedido = d.pedidoDemo;
  const esperando = pedido?.pendiente ?? false;

  // Mientras la PC la arma, se vuelve a leer el lead hasta que aparezca el resultado.
  const revisar = useRef(onCambio);
  revisar.current = onCambio;
  useEffect(() => {
    if (!esperando) return;
    const reloj = setInterval(() => revisar.current(), REVISION_MS);
    return () => clearInterval(reloj);
  }, [esperando]);

  if (d.optOut) return null;

  if (esperando) {
    const demora = Date.now() - new Date(pedido!.creadoEn).getTime() > 3 * 60_000;
    return (
      <div className={s.pedido}>
        <Badge tone="brand" size="sm" dot>
          Armando la demo…
        </Badge>
        <Text size="xs" tone="muted">
          {demora
            ? `La pidió ${pedido!.por ?? "alguien"} ${hace(pedido!.creadoEn)}. La arma la PC: si está apagada, sale cuando se prenda.`
            : "Lee el perfil, elige un aviso y copia las fotos. Tarda menos de un minuto."}
        </Text>
      </div>
    );
  }

  const pedirLink = conLink || !d.perfilDemo;
  const pedir = (otraPropiedad: boolean) =>
    ejecutar(
      "demo",
      () => api<{ yaPedida: boolean }>(`/api/leads/${d.id}/demo`, { metodo: "POST", cuerpo: { link: pedirLink ? link : null, otraPropiedad } }),
      (r) => (r.yaPedida ? "La demo ya estaba pedida" : { title: "Demo pedida", message: "La arma la PC; aparece acá en menos de un minuto." }),
    ).then((r) => {
      if (r) {
        setConLink(false);
        setLink("");
      }
    });
  // Un fallo viejo no se muestra si después igual se armó (la corrida diaria, otro pedido).
  const vigente = pedido && !(d.demo && d.demo.creadaEn > pedido.creadoEn);
  const fallo = vigente && pedido.resultado !== "armada" ? motivoPedido(pedido.resultado, pedido.motivo) : null;

  return (
    <div className={s.pedido}>
      {fallo ? (
        <Text size="xs" tone="danger">
          {fallo}
        </Text>
      ) : null}
      {pedirLink ? (
        <Input
          label="Link de su perfil (Zonaprop, Mercado Libre, Argenprop o BuscadorProp)"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://www.zonaprop.com.ar/inmobiliarias/…-inmuebles.html"
        />
      ) : null}
      <div className={s.botonesPedido}>
        {d.demo ? (
          <Button size="sm" variant="outline" icon="refresh-cw" pending={enCurso === "demo"} disabled={pedirLink && !link.trim()} onClick={() => pedir(true)}>
            {conLink ? "Rearmar con ese perfil" : "Otra propiedad"}
          </Button>
        ) : (
          <Button size="sm" icon="sparkles" pending={enCurso === "demo"} disabled={pedirLink && !link.trim()} onClick={() => pedir(false)}>
            Generar demo
          </Button>
        )}
        {d.perfilDemo ? (
          <Button size="sm" variant="ghost" onClick={() => setConLink(!conLink)}>
            {conLink ? "Usar el perfil que tiene" : "Usar otro perfil"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function Botones({ demo }: { demo: DemoLead }) {
  const { mostrar } = useToast();
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(demo.url);
      mostrar({ title: "Link de la demo copiado", message: "Pegalo en la conversación de Chatwoot.", tone: "success" });
    } catch {
      mostrar({ title: "No se pudo copiar", message: demo.url, tone: "warning", duracion: 10000 });
    }
  };
  return (
    <div className={s.botones}>
      <Button size="sm" icon="copy" onClick={copiar}>
        Copiar link
      </Button>
      <Button size="sm" variant="outline" iconAfter="external-link" href={`${demo.url}?equipo=1`} target="_blank">
        Ver
      </Button>
    </div>
  );
}

function Abierta({ demo }: { demo: DemoLead }) {
  return demo.visitas > 0 ? (
    <Badge tone="success" size="sm" dot>
      {demo.visitas === 1 ? "La abrió" : `La abrió ${demo.visitas} veces`}
    </Badge>
  ) : (
    <Badge tone="neutral" size="sm">
      Sin abrir
    </Badge>
  );
}

/* Para la ficha completa. Sin demo, dice por qué: la última novedad de la bitácora manda. */
export function TarjetaDemo({ d, onCambio }: { d: DetalleLead; onCambio: () => void }) {
  const demo = d.demo;
  const ultima = d.eventos.find((e) => e.tipo === "demo_fallida" || e.tipo === "demo_armada" || e.tipo === "demo_vencida");
  const sinDemo =
    ultima?.tipo === "demo_fallida"
      ? motivoDemo(ultima.datos.motivo)
      : ultima?.tipo === "demo_vencida"
        ? "Tuvo una demo y venció."
        : d.perfilDemo
          ? `Tiene perfil en ${d.perfilDemo.portal}: la demo se arma en la próxima corrida, o ahora con «Generar demo».`
          : "Sin perfil en un portal todavía: pegá el link de su perfil para armarla.";
  // Un pedido en curso o que falló ya dice qué pasa: no se repite abajo de "Demo".
  const pedidoManda = d.pedidoDemo !== null && (d.pedidoDemo.pendiente || d.pedidoDemo.resultado !== "armada");
  return (
    <Card padding="md">
      <div className={s.cuerpo}>
        <div className={s.cabecera}>
          <Heading level={2} size="sm">
            Demo
          </Heading>
          {demo ? <Abierta demo={demo} /> : null}
        </div>
        {demo ? (
          <>
            <Text size="sm" weight="semibold" tone="strong">
              {demo.resumen}
            </Text>
            {demo.titulo ? (
              <Text size="xs" tone="muted">
                “{demo.titulo}”, con {demo.fotos} fotos de su aviso en {nombrePortal(demo.fuente)}.
              </Text>
            ) : null}
            <Text size="xs" tone="muted">
              {demo.abiertaUltimaEn ? `Última visita ${hace(demo.abiertaUltimaEn)}. ` : "Todavía no la abrió. "}
              Vence el {fecha(demo.venceEn)}.
            </Text>
            <Botones demo={demo} />
            <Button size="sm" variant="ghost" iconAfter="external-link" href={demo.avisoUrl} target="_blank" className={s.aviso}>
              El aviso en {nombrePortal(demo.fuente)}
            </Button>
          </>
        ) : pedidoManda ? null : (
          <Text size="sm" tone="muted">
            {sinDemo}
          </Text>
        )}
        <PedirDemo d={d} onCambio={onCambio} />
      </div>
    </Card>
  );
}

/* Para el detalle del pipeline, arriba de la conversación. */
export function FranjaDemo({ demo }: { demo: DemoLead }) {
  return (
    <div className={s.franja}>
      <div className={s.franjaTexto}>
        <div className={s.cabecera}>
          <Text as="span" size="sm" weight="semibold" tone="strong">
            Su demo
          </Text>
          <Abierta demo={demo} />
        </div>
        <Text as="span" size="xs" tone="muted">
          {demo.resumen}
          {demo.abiertaUltimaEn ? ` · última visita ${hace(demo.abiertaUltimaEn)}` : ""}
        </Text>
      </div>
      <Botones demo={demo} />
    </div>
  );
}
